import { CdkDrag } from "@angular/cdk/drag-drop";
import { Injectable } from '@angular/core';
import { formatInTimeZone } from 'date-fns-tz';
import exifr from 'exifr';
import moment, { Moment } from 'moment';
import { Model } from 'src/app/interfaces/api/model';
import { CompatibleSchedule, NormalizedSchedule, Schedule } from 'src/app/interfaces/api/project';
import { Config } from 'src/app/interfaces/shared/config';


interface WorkingHours {
    startDate: string;
    endDate: string;
}

@Injectable({
    providedIn: 'root'
})

export class Tools {
    lastAlert: any = {};
    timeoutAlert: any;
    alertPaused: boolean = false;
    fullscreenModal: any;

    constructor() { }

    isWithinOfflineDelay(tracker: any, configs?: any[]) {
        let unit: any = "seconds";
        let time = 1;

        if (tracker.config && tracker.config.offlineDelay) {
            time = tracker.config.offlineDelay;
        } else if (tracker.offlineDelay) {
            time = tracker.offlineDelay;
        } else if (configs && configs.length > 0 && tracker.type) {
            let defaultConfigs = configs && configs.filter(config => /Default/i.test(config.name));
            let type: string = tracker.type;
            const matchedConfig = defaultConfigs.find(config => config.name.includes(type));
            if (matchedConfig && matchedConfig.offlineDelay)
                time = matchedConfig.offlineDelay;
            else
                unit = "minutes";
        } else {
            unit = "minutes";
        }

        var age = moment().diff(tracker.lastUpdate, unit, true);

        if (age && age < time) {
            return true;
        } else {
            return false;
        }
    }

    normalizeProjectDay(date: Moment, schedule: any, isStartDate = true): [Moment, boolean] {
        let day = moment(date).format('ddd');
        let scheduleStart = schedule[day.toLowerCase()];
        if (!scheduleStart || scheduleStart.start || scheduleStart.start.length || !scheduleStart.end || !scheduleStart.end.length) return [date, false];
        let startingTime = moment(date).startOf('day').add(scheduleStart.start[0], 'hours').add(scheduleStart.start[1], 'minutes');
        let endingTime = moment(date).startOf('day').add(scheduleStart.end[0], 'hours').add(scheduleStart.end[1], 'minutes');

        if (moment(date).isBetween(startingTime, endingTime))
            return [date, true];

        if (isStartDate)
            return [startingTime, true]; //moment(startingTime).format();

        return [endingTime, true]; // moment(endingTime).format();
    }

    normalizeDates(startDate: string, endDate: string | null = null, startDateChanged = true, schedule = null) {
        let startMoment = moment(startDate);
        let endMoment = endDate ? moment(endDate) : startMoment.startOf("day").hour(20);
        let startDateHasSchedule = false;
        let endDateHasSchedule = false;

        if (schedule) {
            [startMoment, startDateHasSchedule] = this.normalizeProjectDay(startMoment, schedule);
            [endMoment, endDateHasSchedule] = this.normalizeProjectDay(endMoment, schedule, false);
        }

        // Normalize start time if no project schedule
        if (!startDateHasSchedule) {
            if (startMoment.isBefore(moment(startMoment).startOf('day').hour(7))) {
                startMoment.startOf('day').hour(7);
            } else if (startMoment.isAfter(moment(startMoment).startOf('day').hour(19).minute(30))) {
                startMoment.startOf('day').add(1, 'day').hour(7);
            }
        }

        // Normalize end time
        if (!endDateHasSchedule) {
            if ((endMoment.isBefore(moment(endMoment).startOf('day').hour(7).minute(30)))
                || (endMoment.isAfter(moment(endMoment).startOf('day').hour(20)))) {
                endMoment.startOf('day').hour(19).minute(30);
            }
        }

        // Ensure startDate is less than endDate
        if (startDateChanged && startMoment.isAfter(endMoment)) {
            endMoment = endMoment.set("date", startMoment.date());
        }
        if (!startDateChanged && endMoment.isBefore(startMoment)) {
            startMoment = startMoment.set("date", endMoment.date())
        }

        return { startDate: startMoment.toISOString(), endDate: endMoment.toISOString() };
    }

    getOffsetForDate(date: string, timezone: string): string {
        // This gives something like +01:00 or +02:00 for THAT date
        return formatInTimeZone(new Date(date), timezone, 'XXX');
    }

    getWorkingHours(date: string, schedule: NormalizedSchedule | null, timezone: string = 'Europe/Paris'): WorkingHours {
        const baseDate = new Date(date);
        const day = formatInTimeZone(baseDate, timezone, 'eee')
            .toLowerCase() as keyof NormalizedSchedule;

        if (!schedule || !schedule[day]) {
            return { startDate: date, endDate: date };
        }

        const offset = this.getOffsetForDate(date, timezone); // <-- suffix like +01:00
        console.log('---> offset :>> ', offset);
        const datePart = date.split('T')[0];

        const buildUTC = (hours: number, minutes: number) => {
            const localString = `${datePart}T${hours
                .toString()
                .padStart(2, '0')}:${minutes
                    .toString()
                    .padStart(2, '0')}:00${offset}`;

            return new Date(localString).toISOString();
        };

        const scheduleForDay = schedule[day];

        if (!scheduleForDay?.start?.length) {
            return {
                startDate: buildUTC(6, 0),
                endDate: buildUTC(19, 0),
            };
        }

        const startDate = buildUTC(
            scheduleForDay.start[0] ? parseInt(scheduleForDay.start[0], 10) : 7,
            scheduleForDay.start[1] ? parseInt(scheduleForDay.start[1], 10) : 0
        );

        const endDate = buildUTC(
            scheduleForDay.end[0] ? parseInt(scheduleForDay.end[0], 10) : 20,
            scheduleForDay.end[1] ? parseInt(scheduleForDay.end[1], 10) : 0
        );

        return { startDate, endDate };
    }

    normalizeSchedule(schedule: CompatibleSchedule | null): NormalizedSchedule | null {
        if (!schedule) return null;

        const normalizedSchedule = {} as NormalizedSchedule;

        const days: (keyof Schedule)[] = [
            "mon", "tue", "wed", "thu", "fri", "sat", "sun"
        ];

        days.forEach((day) => {
            const value = schedule[day];

            // new format
            if (
                typeof value === "object" &&
                value !== null &&
                "enable" in value &&
                "times" in value
            ) {
                const { enable, times } = value;

                if (enable && times.length > 0) {
                    const first = times[0];
                    const last = times[times.length - 1];

                    if (first && last) {
                        normalizedSchedule[day] = {
                            start: first[0].split(":"),
                            end: last[1].split(":"),
                        };
                        return;
                    }
                }

                normalizedSchedule[day] = { start: [], end: [] };
                return;
            }

            // old format
            if (Array.isArray(value) && value.length >= 2) {
                normalizedSchedule[day] = {
                    start: value[0] ? value[0].split(":") : [],
                    end: value[1] ? value[1].split(":") : [],
                };
                return;
            }

            // fallback
            normalizedSchedule[day] = { start: [], end: [] };
        });

        return normalizedSchedule;
    }

    formatDateFR(date: string): string {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }

    /** Determines whether an event is a touch event. */
    isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
        return event.type.startsWith('touch');
    }

    isInsideDragClientRect(hovered: CdkDrag,
        x: number, y: number,
        width: number = 0, height: number = 0,
        offsetTop: number = 0, offsetLeft: number = 0) {
        let hoveredElWidth = hovered.data.width;
        let hoveredElHeight = hovered.data.height;
        let diffWidth = Math.abs(hoveredElWidth - width);
        let diffHeight = Math.abs(hoveredElHeight - height);

        let { top, bottom, left, right } = hovered.element.nativeElement.getBoundingClientRect();

        top = offsetTop;
        left = offsetLeft;
        if (!right || !bottom) return false;
        right = left + width;
        bottom = top + height;
        return y >= top && y <= bottom && x >= left && x <= right;
    }

    calculateOffsets(element: HTMLElement) {
        const rect = element.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft || document.body.scrollLeft;
        const offsetTop = rect.top + scrollTop;
        const offsetLeft = rect.left + scrollLeft;

        return { offsetTop, offsetLeft };
    }


    // bytes convert
    public bytesToString(buffer: ArrayBuffer | Uint8Array): string {
        const uint8 = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        return new TextDecoder().decode(uint8);
    }

    bytesToInt(x: number[]) {
        var val = 0;
        for (var i = 0; i < x.length; ++i) {
            val += x[i] ?? 0;
            if (i < x.length - 1) {
                val = val << 8;
            }
        }
        return val;
    }

    bytesToHex(array: number[]) {
        let result = ''
        for (let i = 0, l = array.length; i < l; i++) {
            result += this.intToHex(array[i] ?? 0)
        }
        return result;
    }

    isHex(str: string) {
        return /^[a-fA-F0-9]{24}$/.test(str);
    }

    //LSB convert
    LSBToInt(x: number[]) {
        var val = 0;
        for (var i = x.length - 1; i >= 0; --i) {

            val += x[i] ?? 0;
            if (i > 0) {
                val = val << 8;
            }
        }
        return val;
    }

    //int convert
    intToBytes(x: number, bytesLength?: number) {
        var bytes = [];
        var i = bytesLength || 8;
        do {
            bytes[--i] = x & (255);
            x = x >> 8;
        } while (i)
        return bytes;
    }

    intToLSB(x: number, bytesLength?: any) {
        var bytes = [];
        var bytesLength = bytesLength || 8;
        var i = 0;
        do {
            bytes[i] = x & (255);
            i++;
            x = x >> 8;
        } while (i < bytesLength)

        return bytes;
    }

    intToHex(num: number) {
        if (num < 256) {
            return Math.abs(num).toString(16);
        }
        return 0;
    }

    //hexadecimal convert
    hexToInt(hex: string) {
        return parseInt(hex, 16);
    }
    //hexadecimal convert
    hexToString(hex: any) {
        hex = hex.toString();
        var str = '';
        for (var n = 0; n < hex.length; n += 2) {
            str += String.fromCharCode(parseInt(hex.substr(n, 2), 16));
        }
        return str;
    }

    //string convert
    stringToBytes(string: string) {
        var array = new Uint8Array(string.length);
        for (var i = 0, l = string.length; i < l; i++) {
            array[i] = string.charCodeAt(i);
        }
        return array;
    }

    bufferToBase64(buffer: any[]) {
        var chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
        var bytes: any[] = buffer,
            i, len = bytes.length,
            base64 = "";

        for (i = 0; i < len; i += 3) {
            base64 += chars[bytes[i] >> 2];
            base64 += chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
            base64 += chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
            base64 += chars[bytes[i + 2] & 63];
        }

        if ((len % 3) === 2) {
            base64 = base64.substring(0, base64.length - 1) + "=";
        }
        else if (len % 3 === 1) {
            base64 = base64.substring(0, base64.length - 2) + "==";
        }

        return base64;
    };

    isEmailValid = (email: string) => {
        return String(email)
            .toLowerCase()
            .trim()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    readonly passwordMinLength: number = 8;
    readonly generatedPasswordLength: number = 12;

    // Règles : 8 caractères minimum, au moins 1 majuscule et 1 chiffre, aucun espace
    isPasswordValid(password: string): boolean {
        return this.getPasswordMissingRules(password).length === 0;
    }

    getPasswordMissingRules(password: string): ('length' | 'uppercase' | 'digit' | 'spaces')[] {
        const missing: ('length' | 'uppercase' | 'digit' | 'spaces')[] = [];

        if (password.length < this.passwordMinLength)
            missing.push('length');

        if (!/[A-Z]/.test(password))
            missing.push('uppercase');

        if (!/\d/.test(password))
            missing.push('digit');

        if (/\s/.test(password))
            missing.push('spaces');

        return missing;
    }

    generatePassword(length: number = this.generatedPasswordLength): string {
        // Caractères ambigus exclus (l, I, O, 0, 1)
        const lowercase = 'abcdefghijkmnopqrstuvwxyz';
        const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
        const digits = '23456789';
        const all = lowercase + uppercase + digits;

        // Un caractère garanti par règle, le reste aléatoire
        const chars: string[] = [uppercase, digits].map(set => this.randomChar(set));

        while (chars.length < Math.max(length, this.passwordMinLength))
            chars.push(this.randomChar(all));

        // Mélange Fisher-Yates pour ne pas laisser les caractères garantis en tête
        for (let i = chars.length - 1; i > 0; i--) {
            const j = this.randomInt(i + 1);
            [chars[i], chars[j]] = [chars[j]!, chars[i]!];
        }

        return chars.join('');
    }

    private randomChar(set: string): string {
        return set.charAt(this.randomInt(set.length));
    }

    // Entier aléatoire dans [0, max[ sans biais de modulo
    private randomInt(max: number): number {
        const limit = Math.floor(0x100000000 / max) * max;
        const buffer = new Uint32Array(1);

        do {
            crypto.getRandomValues(buffer);
        } while (buffer[0]! >= limit);

        return buffer[0]! % max;
    }

    getBatteryColor(trackerBattery: number): string {
        if (trackerBattery >= 70) {
            return '#4CAF50';
        } else if (trackerBattery >= 40) {
            return '#aeb82e';
        } else if (trackerBattery >= 10) {
            return '#FFA500';
        } else {
            return '#F44336';
        }
    }

    getShackleColor(shackleBattery: number): string {
        if (shackleBattery >= 40) {
            return '#4CAF50';
        } else if (shackleBattery >= 10) {
            return '#aeb82e';
        } else {
            return '#F44336';
        }
    }

    renameDuplicates(arr: string[]) {
        const counts: Record<string, number> = {};
        const result = [];

        for (const item of arr) {
            if (!counts[item]) {
                counts[item] = 1;
                result.push(item);
            } else {
                const newName = `${item}(${counts[item]})`;
                counts[item]++;
                result.push(newName);
            }
        }

        return result;
    }

    base64ToFile(base64: string, filename: string): File | null {
        const arr = base64.split(',');

        if (!arr[0] || !arr[1])
            return null;

        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    }

    toId(value: any): string | undefined {
        return value?.id ?? value;
    }

    extractNames(fullName: string): { firstName: string; lastName: string } {
        const nameParts = fullName?.trim().split(/\s+/) || [];
        const particles = ['de', 'du', 'des', 'le', 'la', 'van', 'von', 'del', 'della', 'di', 'da', 'mac', 'mc', "d'", "l'"];

        if (nameParts.length === 0) {
            return { firstName: '', lastName: '' };
        }

        if (nameParts.length === 1) {
            return {
                firstName: nameParts[0] || '',
                lastName: ''
            };
        }

        let lastNameStartIndex = nameParts.length - 1;

        for (let i = nameParts.length - 2; i >= 0; i--) {
            const word = nameParts[i]?.toLowerCase() || '';

            if (particles.includes(word) || particles.includes(word.replace(/'/g, "'"))) {
                lastNameStartIndex = i;
            } else {
                break;
            }
        }

        const firstName = nameParts.slice(0, lastNameStartIndex).join(' ');
        const lastName = nameParts.slice(lastNameStartIndex).join(' ');

        return {
            firstName: firstName || '',
            lastName: lastName || ''
        };
    }

    hexToRGBA(hex: string, alpha: number): string {
        const r = parseInt(hex.substring(1, 3), 16);
        const g = parseInt(hex.substring(3, 5), 16);
        const b = parseInt(hex.substring(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    getDarkerColor(hex: string, factor: number = 0.5): string {
        let r = parseInt(hex.substring(1, 3), 16);
        let g = parseInt(hex.substring(3, 5), 16);
        let b = parseInt(hex.substring(5, 7), 16);

        r = Math.floor(r * factor);
        g = Math.floor(g * factor);
        b = Math.floor(b * factor);

        return `rgb(${r}, ${g}, ${b})`;
    }

    /**
    * Check if a string is a valid hex color (e.g., #FFF or #FFFFFF)
    */
    isHexColor(colorValue: string | null | undefined): boolean {
        return !!colorValue && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(colorValue.trim());
    }

    /**
     * Get the hex color value based on a key
     * @param colorKey - Key of the color (can be a name or a hex value)
     * @param listColor - List colors
     * @param defaultColor - Color for default value (if colorKey is null/undefined/empty)
     */
    getColorValue(
        colorKey: string | null | undefined,
        listColor: { name: string; value: string }[],
        defaultColor: string = Config.colors.primary
    ): string {
        if (!colorKey?.trim()) {
            return defaultColor;
        }

        if (this.isHexColor(colorKey)) {
            return colorKey;
        }

        const normalized = colorKey.toLowerCase();
        const match = listColor.find(c => c.name.toLowerCase() === normalized);

        if (match) {
            return match.value;
        }

        const configColor = (Config.colors as any)[normalized];

        if (configColor) {
            return configColor;
        }

        return colorKey;
    }

    /**
     * Get the display name of a color based on a key which can be either a name or a hex value.
     * @param colorKey - Key of the color (can be a name or a hex value)
     * @param listColor - List of available colors with their names and hex values
     */
    getColorName(
        colorKey: string | null | undefined,
        listColor: { name: string; value: string }[]
    ): string {
        if (!colorKey?.trim()) {
            return 'Primary';
        }

        if (this.isHexColor(colorKey)) {
            const matchByValue = listColor.find(c =>
                c.value.toLowerCase() === colorKey.toLowerCase()
            );
            if (matchByValue) {
                return matchByValue.name;
            }

            return colorKey.toUpperCase();
        }

        const normalized = colorKey.toLowerCase();

        const matchByName = listColor.find(c => c.name.toLowerCase() === normalized);
        if (matchByName) {
            return matchByName.name;
        }

        const matchByValue = listColor.find(c => c.value.toLowerCase() === colorKey.toLowerCase());
        if (matchByValue) {
            return matchByValue.name;
        }

        return colorKey.charAt(0).toUpperCase() + colorKey.slice(1);
    }

    formatDuration(seconds: number | string | undefined): string {
        if (!seconds) return '0s';

        const sec = typeof seconds === 'string' ? parseInt(seconds, 10) : seconds;

        if (sec < 60) {
            return `${sec}s`;
        }

        const minutes = Math.floor(sec / 60);
        const remainingSeconds = sec % 60;

        return `${minutes}m ${remainingSeconds}s`;
    }

    /**
      * Parse a date encoded in a filename following the
      * `YYYY-MM-DD_HH-MM-SS` (or space-separated) convention.
      * Returns an ISO string, or undefined if no valid date is found.
      */
    parseDateFromFilename(filename: string): string | undefined {
        const match = filename.match(/(\d{4})-(\d{2})-(\d{2})[_\s](\d{2})-(\d{2})-(\d{2})/);
        if (!match) return undefined;
        const [, y, mo, d, h, mi, s] = match;
        const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}`);
        return isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    /**
      * Resolve an image's creation date from its metadata: the EXIF "date taken"
      * (DateTimeOriginal — the value under Origine → Prise de vue in Windows file
      * details), then CreateDate/ModifyDate. Falls back to a date encoded in the
      * filename and finally to now.
      */
    async getFileCreationDate(file: File): Promise<string> {
        if (file.type.startsWith("image/")) {
            try {
                const exif = await exifr.parse(file, ["DateTimeOriginal", "CreateDate", "ModifyDate"]);
                const taken = exif?.DateTimeOriginal ?? exif?.CreateDate ?? exif?.ModifyDate;
                if (taken instanceof Date && !isNaN(taken.getTime())) return taken.toISOString();
            } catch {
                // No/invalid EXIF metadata — fall back below.
            }
        }
        return this.parseDateFromFilename(file.name) ?? new Date().toISOString();
    }
}

export function normalizeModel(model: Model): Model {
  const it = model.imageTransform;
  const lat = model.mapCenter?.lat || model.origin.position.mapLat || model.origin.position.lat || 0;
  const lon = model.mapCenter?.lon || model.origin.position.mapLon || model.origin.position.lon || 0;

  return {
    ...model,
    mapCenter: { lat, lon },
    imageTransform: {
      angle: it?.angle ?? model.mapImageAngle ?? 0,
      opacity: it?.opacity ?? model.opacity ?? 1,
      masks: it?.masks ?? [],
    },
  };
}
